import { buildLifecycleWorkflowTrail } from "./workflowStepsBadge";
import {
  RESIGNATION_APPROVAL_LABEL,
  TERMINATION_APPROVAL_LABEL,
  resolveChecklistDisplayLabel,
  resolveSeparationApprovalLabel,
} from "./deskActionMatrix";

export const CONFIRMATION_APPROVAL_LABEL = "Confirmation Approval";
export const TRANSFER_APPROVAL_LABEL = "Transfer Approval";
/** @deprecated Use RESIGNATION_APPROVAL_LABEL / TERMINATION_APPROVAL_LABEL */
export const SEPARATION_APPROVAL_LABEL = "Exit / Separation Approval";
export { RESIGNATION_APPROVAL_LABEL, TERMINATION_APPROVAL_LABEL };
export const RESIGNATION_REQUEST_LABEL = "Resignation";
export const CONFIRMATION_REQUEST_LABEL = "Confirmation";
export const TRANSFER_REQUEST_LABEL = "Transfer";

const PENDING_API_STATUSES = new Set([
  "pending_approval",
  "on_hold",
  "extended",
  "cancellation_pending",
  "clearance_pending",
  "fnf_pending",
  "draft",
]);

const APPROVED_API_STATUSES = new Set([
  "approved",
  "applied",
  "completed",
  "confirmed",
  "clearance_pending",
  "fnf_pending",
]);

export function mapLifecycleApiStatusToDeskStatus(apiStatus, options = {}) {
  if (options.notifyOnlyFyi) {
    return options.acknowledged ? "Approved" : "Pending";
  }
  const s = String(apiStatus || "").toLowerCase();
  if (s === "rejected" || s === "cancelled") return "Rejected";
  if (APPROVED_API_STATUSES.has(s) && options.deskMode === "completed") return "Approved";
  if (APPROVED_API_STATUSES.has(s) && options.deskMode === "checklist") {
    const taskStatus = String(options.checklistStatus || "").toLowerCase();
    if (taskStatus === "completed" || taskStatus === "done") return "Approved";
    if (taskStatus === "rejected" || taskStatus === "sent_back") return "Rejected";
    return "Pending";
  }
  if (PENDING_API_STATUSES.has(s)) return "Pending";
  if (s === "completed") return "Approved";
  return "Pending";
}

function baseEmployeeFields(row) {
  const emp = row.employee || {};
  const user = emp.user || {};
  const name = row.employeeName || row.candidateName || user.name || "Employee";
  const code = row.employeeCode || emp.empCode || "-";
  return { name, code, emp, user };
}

function buildLifecycleDeskItem({
  row,
  actionLabel,
  lifecycleKind,
  requestIdPrefix,
  requestIdField,
  requestId,
  deskMode,
  options = {},
}) {
  const { name, code } = baseEmployeeFields(row);
  const notifyOnlyFyi = !!options.notifyOnlyFyi;
  const deskStatus = mapLifecycleApiStatusToDeskStatus(row.status, {
    notifyOnlyFyi,
    acknowledged: options.acknowledged,
    deskMode,
    checklistStatus: row.checklistTask?.status,
  });
  const workflowTrail = notifyOnlyFyi
    ? [
        {
          status: "Notification",
          role: "Notification",
          label: "N",
          displayText: "Notification",
          isNotification: true,
          stepOrder: 1,
        },
      ]
    : buildLifecycleWorkflowTrail(row);

  const checklistTask = row.checklistTask || null;
  const isChecklist = deskMode === "checklist" && checklistTask;
  const taskName = checklistTask?.name || "";
  const displayActionLabel = isChecklist
    ? resolveChecklistDisplayLabel({
        lifecycleKind,
        separationType: row.separationType,
        taskName,
        actionLabel,
      })
    : actionLabel;

  const baseForm = buildLifecycleFormData(row, lifecycleKind);
  const formData = isChecklist
    ? {
        ...baseForm,
        taskName: taskName || "Checklist task",
        checklistName: taskName || "",
        completedComments: checklistTask?.completedComments || "",
      }
    : baseForm;

  return {
    requestId:
      row.deskItemKey ||
      `${requestIdPrefix}_${requestId}${isChecklist ? `_chk_${checklistTask.id}` : ""}${
        notifyOnlyFyi ? "_notify" : ""
      }`,
    panelKey: options.panelKey || (deskMode === "request" ? "review" : "approvals"),
    actionLabel: displayActionLabel,
    lifecycleKind,
    deskMode,
    source: "api",
    notifyOnlyFyi,
    [requestIdField]: requestId,
    checklistItemId: checklistTask?.id || row.checklistItemId || null,
    checklistTaskId: checklistTask?.id || row.checklistItemId || null,
    checklistTask: checklistTask || null,
    checklistName: taskName || null,
    linkedEmployeeId: row.employeeId || null,
    userId: row.createdByUserId || row.userId || null,
    userName: name,
    userEmail: row.employee?.user?.email || "",
    employeeId: code !== "-" ? code : String(row.employeeId || ""),
    employeeCode: code !== "-" ? code : null,
    status: options.deskStatus || deskStatus,
    submittedAt: row.submittedAt || row.createdAt,
    updatedAt: row.updatedAt,
    pendingWith: notifyOnlyFyi
      ? options.acknowledged
        ? "Acknowledged (notification only)"
        : "Notification only — no approval action"
      : row.pendingAssigneeDisplay || row.pendingLabel || row.pendingRole || "",
    assigneeDisplay: notifyOnlyFyi
      ? "Notification"
      : row.pendingAssigneeDisplay ||
        row.assigneeDisplay ||
        row.pendingLabel ||
        row.pendingRole ||
        "",
    pendingAssigneeDisplay: row.pendingAssigneeDisplay || null,
    currentStep: row.currentStep,
    totalSteps: row.totalSteps,
    workflowSnapshot: row.workflowSnapshot || null,
    approvals: row.approvals || [],
    policyName: row.policySnapshot?.policyName || row.policySnapshot?.name || null,
    separationType: row.separationType || null,
    requestType: row.requestType || null,
    transferType: row.transferType || null,
    effectiveDate: row.effectiveDate || null,
    lastWorkingDay: row.lastWorkingDay || row.relievingDate || null,
    separationDate: row.separationDate || null,
    expectedConfirmationDate: row.expectedConfirmationDate || null,
    canApprove: !notifyOnlyFyi && deskStatus === "Pending" && deskMode === "approval",
    readOnlyApproval:
      notifyOnlyFyi ? !!options.acknowledged : deskStatus !== "Pending" || deskMode === "completed",
    formData,
    workflowTrail,
    workflowSteps: workflowTrail,
  };
}

function buildLifecycleFormData(row, lifecycleKind) {
  const { name, code } = baseEmployeeFields(row);
  const policy = row.policySnapshot || {};
  // Keep form payload business-facing only (no workflow keys / internal ids).
  const base = {
    employeeName: name,
    employeeCode: code,
    policyName: policy.policyName || policy.name || "",
  };
  if (lifecycleKind === "confirmation") {
    return {
      ...base,
      expectedConfirmationDate: row.expectedConfirmationDate || "",
      probationEndDate: row.probationEndDate || "",
    };
  }
  if (lifecycleKind === "transfer") {
    return {
      ...base,
      effectiveDate: row.effectiveDate || "",
      transferType: row.transferType || "",
    };
  }
  if (lifecycleKind === "separation") {
    return {
      ...base,
      separationDate: row.separationDate || "",
      lastWorkingDay: row.lastWorkingDay || row.relievingDate || "",
      reason: row.reason || "",
    };
  }
  return base;
}

export function mapConfirmationRequestToDeskItem(row, options = {}) {
  return buildLifecycleDeskItem({
    row,
    actionLabel: CONFIRMATION_APPROVAL_LABEL,
    lifecycleKind: "confirmation",
    requestIdPrefix: "confirmation",
    requestIdField: "confirmationRequestId",
    requestId: row.id,
    deskMode: row.deskMode || "approval",
    options,
  });
}

export function mapConfirmationChecklistToDeskItem(row) {
  return mapConfirmationRequestToDeskItem(
    {
      ...row,
      deskMode: "checklist",
      checklistTask: row.checklistTask,
      deskItemKey: row.deskItemKey || `confirmation_chk_${row.id}_${row.checklistTask?.id || row.checklistItemId || "task"}`,
    },
    {}
  );
}

export function mapTransferRequestToDeskItem(row, options = {}) {
  return buildLifecycleDeskItem({
    row,
    actionLabel: TRANSFER_APPROVAL_LABEL,
    lifecycleKind: "transfer",
    requestIdPrefix: "transfer",
    requestIdField: "transferRequestId",
    requestId: row.id,
    deskMode: row.deskMode || "approval",
    options,
  });
}

export function mapTransferChecklistToDeskItem(row) {
  return mapTransferRequestToDeskItem(
    {
      ...row,
      deskMode: "checklist",
      checklistTask: row.checklistTask,
      deskItemKey: row.deskItemKey || `transfer_chk_${row.id}_${row.checklistTask?.id || row.checklistItemId || "task"}`,
    },
    {}
  );
}

export function mapSeparationRequestToDeskItem(row, options = {}) {
  const label = resolveSeparationApprovalLabel(row.separationType);
  return buildLifecycleDeskItem({
    row,
    actionLabel: label,
    lifecycleKind: "separation",
    requestIdPrefix: "separation",
    requestIdField: "separationRequestId",
    requestId: row.id,
    deskMode: row.deskMode || "approval",
    options,
  });
}

export function mapSeparationChecklistToDeskItem(row) {
  return mapSeparationRequestToDeskItem(
    {
      ...row,
      deskMode: "checklist",
      checklistTask: row.checklistTask,
      deskItemKey: row.deskItemKey || `separation_chk_${row.id}_${row.checklistTask?.id || row.checklistItemId || "task"}`,
    },
    {}
  );
}

export function mapSeparationMyRequestToDeskItem(row) {
  const deskStatus =
    row.deskStatus === "Approved"
      ? "Approved"
      : row.deskStatus === "Rejected"
        ? "Rejected"
        : "Pending";
  return buildLifecycleDeskItem({
    row,
    actionLabel: RESIGNATION_REQUEST_LABEL,
    lifecycleKind: "separation",
    requestIdPrefix: "separation_req",
    requestIdField: "separationRequestId",
    requestId: row.id,
    deskMode: "request",
    options: { panelKey: "review", deskStatus },
  });
}

export function mapConfirmationMyRequestToDeskItem(row) {
  const deskStatus =
    row.deskStatus === "Approved"
      ? "Approved"
      : row.deskStatus === "Rejected"
        ? "Rejected"
        : "Pending";
  return buildLifecycleDeskItem({
    row,
    actionLabel: CONFIRMATION_REQUEST_LABEL,
    lifecycleKind: "confirmation",
    requestIdPrefix: "confirmation_req",
    requestIdField: "confirmationRequestId",
    requestId: row.id,
    deskMode: "request",
    options: { panelKey: "review", deskStatus },
  });
}

export function mapTransferMyRequestToDeskItem(row) {
  const deskStatus =
    row.deskStatus === "Approved"
      ? "Approved"
      : row.deskStatus === "Rejected"
        ? "Rejected"
        : "Pending";
  return buildLifecycleDeskItem({
    row,
    actionLabel: TRANSFER_REQUEST_LABEL,
    lifecycleKind: "transfer",
    requestIdPrefix: "transfer_req",
    requestIdField: "transferRequestId",
    requestId: row.id,
    deskMode: "request",
    options: { panelKey: "review", deskStatus },
  });
}

export function mapLifecycleDeskApiRows(rows = []) {
  return rows.map((row) => {
    const kind = row.lifecycleKind || "confirmation";
    if (row.deskMode === "checklist") {
      if (kind === "confirmation") return mapConfirmationChecklistToDeskItem(row);
      if (kind === "transfer") return mapTransferChecklistToDeskItem(row);
      if (kind === "separation") return mapSeparationChecklistToDeskItem(row);
    }
    if (row.deskMode === "request") {
      if (kind === "separation") return mapSeparationMyRequestToDeskItem(row);
      if (kind === "confirmation") return mapConfirmationMyRequestToDeskItem(row);
      if (kind === "transfer") return mapTransferMyRequestToDeskItem(row);
    }
    if (kind === "confirmation") return mapConfirmationRequestToDeskItem(row);
    if (kind === "transfer") return mapTransferRequestToDeskItem(row);
    if (kind === "separation") return mapSeparationRequestToDeskItem(row);
    return mapConfirmationRequestToDeskItem(row);
  });
}

export function isLifecycleDeskRequest(request) {
  return (
    request?.source === "api" &&
    ["confirmation", "transfer", "separation"].includes(request?.lifecycleKind)
  );
}

export function isConfirmationDeskRequest(request) {
  return isLifecycleDeskRequest(request) && request.lifecycleKind === "confirmation";
}

export function isTransferDeskRequest(request) {
  return isLifecycleDeskRequest(request) && request.lifecycleKind === "transfer";
}

export function isSeparationDeskRequest(request) {
  return isLifecycleDeskRequest(request) && request.lifecycleKind === "separation";
}

export function isLifecycleChecklistDeskRequest(request) {
  return isLifecycleDeskRequest(request) && request.deskMode === "checklist";
}

export function isSeparationReviewRequest(request) {
  return (
    request?.panelKey === "review" &&
    request?.lifecycleKind === "separation" &&
    request?.deskMode === "request"
  );
}
